import React, { useState, useEffect, useCallback } from "react";
import {
   Input, Button, Table, Modal, Form, Typography, Select, Avatar
} from "antd";
import { useDispatch } from "react-redux";
import axios from "axios";
import moment from "moment";
import toast from "react-hot-toast";
import Layout from "../../components/layout";
import { showLoading, hideLoading } from "../../redux/alertsSlice";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  SearchOutlined, FileExcelOutlined, FilePdfOutlined,
  FilterOutlined, ClearOutlined, StopOutlined,
  CalendarOutlined, UserOutlined, MedicineBoxOutlined,
  EnvironmentOutlined, CloseCircleOutlined
} from "@ant-design/icons";

const { Text, Title } = Typography;

/* ─── Tokens ─────────────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ─── Status config ───────────────────────────────────────────── */
const STATUS_CONFIG = {
  pending:    { color: "#b45309", bg: "#fef9c3" },
  approved:   { color: EMERALD,   bg: TEAL_LIGHT },
  "follow-up":{ color: "#7c3aed", bg: "#f5f3ff"  },
  completed:  { color: "#16a34a", bg: "#dcfce7"  },
  rejected:   { color: "#dc2626", bg: "#fee2e2"  },
  canceled:   { color: "#dc2626", bg: "#fee2e2"  },
};
const getStatusCfg = (s) => STATUS_CONFIG[s] || { color: MUTED, bg: SURFACE };

/* ─── Style injection ─────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("appt-cancel-styles")) return;
  const el = document.createElement("style");
  el.id = "appt-cancel-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    .ac-root { font-family:'DM Sans',sans-serif; background:${SURFACE}; min-height:100vh; }
    .ac-wrap { max-width:1200px; margin:0 auto; padding:32px 24px 64px; }

    /* Hero */
    .ac-hero {
      background:linear-gradient(135deg,${SLATE} 0%,${SLATE_MID} 100%);
      border-radius:24px; padding:36px 44px;
      position:relative; overflow:hidden; margin-bottom:28px;
    }
    .ac-hero::before {
      content:''; position:absolute; top:-80px; right:-80px;
      width:300px; height:300px; border-radius:50%;
      background:radial-gradient(circle,${TEAL}44 0%,transparent 70%);
      filter:blur(32px); pointer-events:none;
    }
    .ac-hero::after {
      content:''; position:absolute; bottom:-60px; left:-40px;
      width:200px; height:200px; border-radius:50%;
      background:radial-gradient(circle,${EMERALD}33 0%,transparent 70%);
      filter:blur(26px); pointer-events:none;
    }
    .ac-eyebrow {
      display:inline-block; padding:4px 12px; border-radius:20px;
      background:${TEAL_LIGHT}; color:${EMERALD};
      font-size:11px; font-weight:700; letter-spacing:2px; text-transform:uppercase;
      font-family:'Sora',sans-serif; margin-bottom:12px;
    }
    .ac-hero-badges { display:flex; gap:8px; flex-wrap:wrap; margin-top:12px; }
    .ac-hero-badge {
      background:rgba(255,255,255,0.12); border-radius:20px;
      padding:4px 12px; font-size:12px; color:rgba(255,255,255,0.75);
      font-family:'DM Sans',sans-serif;
    }

    /* Toolbar */
    .ac-toolbar {
      background:${WHITE}; border-radius:16px;
      box-shadow:0 4px 20px rgba(0,0,0,.06);
      padding:16px 20px; margin-bottom:20px;
      display:flex; align-items:center; gap:12px; flex-wrap:wrap;
    }
    .ac-root .ac-search.ant-input-affix-wrapper {
      border-radius:10px !important; height:40px !important;
      border:1.5px solid #e2e8f0 !important; flex:1; min-width:180px;
      font-family:'DM Sans',sans-serif !important; font-size:13px !important;
      transition:border-color .2s,box-shadow .2s !important;
    }
    .ac-root .ac-search.ant-input-affix-wrapper:hover,
    .ac-root .ac-search.ant-input-affix-wrapper-focused {
      border-color:${TEAL} !important; box-shadow:0 0 0 3px ${TEAL}22 !important;
    }
    .ac-root .ac-search .ant-input { font-family:'DM Sans',sans-serif !important; font-size:13px !important; }

    .ac-root .ac-status-select .ant-select-selector {
      border-radius:10px !important; height:40px !important;
      border:1.5px solid #e2e8f0 !important;
      display:flex !important; align-items:center !important;
      font-family:'DM Sans',sans-serif !important; font-size:13px !important;
      transition:border-color .2s,box-shadow .2s !important;
    }
    .ac-root .ac-status-select .ant-select-selection-item,
    .ac-root .ac-status-select .ant-select-selection-placeholder { line-height:38px !important; }
    .ac-root .ac-status-select.ant-select-focused .ant-select-selector,
    .ac-root .ac-status-select .ant-select-selector:hover {
      border-color:${TEAL} !important; box-shadow:0 0 0 3px ${TEAL}22 !important;
    }

    .ac-btn {
      height:40px !important; padding:0 16px !important; border-radius:10px !important;
      font-size:13px !important; font-family:'DM Sans',sans-serif !important; font-weight:600 !important;
      border:1.5px solid #e2e8f0 !important; background:${WHITE} !important;
      color:${SLATE_MID} !important; transition:border-color .2s,color .2s !important;
      display:flex !important; align-items:center !important; gap:6px !important;
    }
    .ac-btn:hover         { border-color:${TEAL} !important; color:${TEAL} !important; }
    .ac-btn-excel:hover   { border-color:#16a34a !important; color:#16a34a !important; }
    .ac-btn-pdf:hover     { border-color:#dc2626 !important; color:#dc2626 !important; }

    /* Table */
    .ac-table-wrap {
      background:${WHITE}; border-radius:20px;
      box-shadow:0 4px 20px rgba(0,0,0,.06); overflow:hidden;
    }
    .ac-root .ant-table-thead > tr > th {
      background:${SURFACE} !important; color:${SLATE_MID} !important;
      font-size:11px !important; font-weight:700 !important;
      letter-spacing:.5px; text-transform:uppercase;
      border-bottom:1.5px solid #f1f5f9 !important;
      font-family:'Sora',sans-serif !important; padding:14px 16px !important;
    }
    .ac-root .ant-table-tbody > tr > td {
      font-size:13px !important; font-family:'DM Sans',sans-serif !important;
      color:${SLATE_MID} !important; padding:12px 16px !important;
      border-bottom:1px solid #f8fafc !important;
    }
    .ac-root .ant-table-tbody > tr:hover > td { background:#f0fdfb !important; }
    .ac-root .ant-pagination { padding:16px !important; }
    .ac-root .ant-pagination-item-active { border-color:${TEAL} !important; }
    .ac-root .ant-pagination-item-active a { color:${TEAL} !important; }

    /* Badge */
    .ac-badge {
      padding:3px 10px; border-radius:20px;
      font-size:11px; font-weight:700; font-family:'Sora',sans-serif;
      display:inline-block;
    }

    /* Cancel action button */
    .ac-cancel-btn {
      height:30px !important; padding:0 12px !important; border-radius:8px !important;
      font-size:12px !important; font-weight:700 !important;
      border:1.5px solid #ef4444 !important;
      color:#ef4444 !important; background:${WHITE} !important;
      font-family:'DM Sans',sans-serif !important; transition:all .2s !important;
    }
    .ac-cancel-btn:hover { background:#ef4444 !important; color:${WHITE} !important; }

    /* ── Modal ── */
    .ac-modal .ant-modal-content { border-radius:20px !important; overflow:hidden; padding:0 !important; }
    .ac-modal .ant-modal-header {
      background:linear-gradient(135deg,#7f1d1d,#dc2626) !important;
      padding:20px 28px !important; border-radius:0 !important; margin:0 !important;
    }
    .ac-modal .ant-modal-title {
      color:${WHITE} !important; font-family:'Sora',sans-serif !important;
      font-weight:700 !important; font-size:16px !important;
    }
    .ac-modal .ant-modal-close { color:rgba(255,255,255,.5) !important; top:18px !important; }
    .ac-modal .ant-modal-close:hover { color:${WHITE} !important; }
    .ac-modal .ant-modal-body { padding:24px 28px !important; }
    .ac-modal .ant-modal-footer { padding:16px 28px !important; border-top:1.5px solid #f1f5f9 !important; }

    /* Appointment strip in modal */
    .ac-appt-strip {
      background:${SURFACE}; border:1.5px solid #f1f5f9;
      border-radius:14px; padding:16px 18px; margin-bottom:20px;
      display:flex; flex-direction:column; gap:10px;
    }
    .ac-strip-row { display:flex; align-items:center; gap:10px; }
    .ac-strip-icon {
      width:28px; height:28px; border-radius:8px; flex-shrink:0;
      display:flex; align-items:center; justify-content:center; font-size:13px;
    }

    /* Modal form */
    .ac-modal .ant-form-item { margin-bottom:16px !important; }
    .ac-modal .ant-form-item-label { padding-bottom:4px !important; }
    .ac-modal .ant-form-item-label > label {
      font-size:12px !important; font-weight:600 !important;
      color:${SLATE_MID} !important; height:20px !important;
    }
    .ac-modal textarea.ant-input {
      border-radius:10px !important; border:1.5px solid #e2e8f0 !important;
      font-family:'DM Sans',sans-serif !important; font-size:13px !important;
      padding:10px 12px !important; transition:border-color .2s !important;
    }
    .ac-modal textarea.ant-input:hover, .ac-modal textarea.ant-input:focus {
      border-color:#ef4444 !important; box-shadow:0 0 0 3px #ef444422 !important;
    }

    /* Modal footer */
    .ac-modal-close-btn {
      height:40px !important; padding:0 20px !important; border-radius:10px !important;
      font-size:13px !important; font-weight:600 !important;
      font-family:'DM Sans',sans-serif !important;
      border:1.5px solid #e2e8f0 !important; color:${SLATE_MID} !important;
    }
    .ac-modal-confirm-btn {
      height:40px !important; padding:0 24px !important; border-radius:10px !important;
      font-size:13px !important; font-weight:700 !important;
      font-family:'Sora',sans-serif !important;
      background:linear-gradient(135deg,#b91c1c,#dc2626) !important;
      border:none !important; color:${WHITE} !important;
      box-shadow:0 4px 12px #ef444444 !important;
    }

    /* Animations */
    @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
    .ac-fade   { animation:fadeUp .5s ease both; }
    .ac-fade-1 { animation-delay:.05s; }
    .ac-fade-2 { animation-delay:.12s; }
  `;
  document.head.appendChild(el);
};

/* ─── Main component ──────────────────────────────────────────── */
const AppointmentCanceled = () => {
  const dispatch = useDispatch();
  const [appointments,        setAppointments]        = useState([]);
  const [filteredInfo,        setFilteredInfo]        = useState({ status: ["approved"] });
  const [sortedInfo,          setSortedInfo]          = useState({});
  const [searchTerm,          setSearchTerm]          = useState("");
  const [statusFilter,        setStatusFilter]        = useState("All");
  const [cancelModalVisible,  setCancelModalVisible]  = useState(false);
  const [selectedRecord,      setSelectedRecord]      = useState(null);
  const [confirmLoading,      setConfirmLoading]      = useState(false);
  const [form] = Form.useForm();

  useEffect(() => { injectStyles(); }, []);

  const getAppointmentData = useCallback(async () => {
    try {
      dispatch(showLoading());
      const res = await axios.get("/api/admin/get-all-appointments", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      dispatch(hideLoading());
      if (res.data.success) setAppointments(res.data.data);
    } catch {
      dispatch(hideLoading());
      toast.error("Failed to load appointments");
    }
  }, [dispatch]);

  useEffect(() => { getAppointmentData(); }, [getAppointmentData]);

  const filteredData = appointments.filter(a => {
    const matchesSearch = a.userInfo?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "All" || a.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleTableChange = (_, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  /* ── Exports ── */
  const exportToCSV = () => {
    const exportData = filteredData.map(a => ({
      Patient: a.userInfo?.name || "N/A",
      "Appointment Service": a.doctorInfo?.service || "N/A",
      "Doctor Name": `${a.doctorInfo?.firstName || ""} ${a.doctorInfo?.lastName || ""}`,
      "Clinic Day": a.doctorInfo?.clinicDay?.join(", ") || "N/A",
      "Scheduled Date": a.schedule_date ? moment(a.schedule_date).format("DD-MM-YYYY") : "Not set",
      Status: a.status,
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Appointments");
    saveAs(new Blob([XLSX.write(wb, { type: "array", bookType: "xlsx" })], { type: "application/octet-stream" }), "appointments.xlsx");
    toast.success("Exported to Excel");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    autoTable(doc, {
      head: [["Patient", "Service", "Doctor", "Clinic Day", "Date", "Status"]],
      body: filteredData.map(a => [
        a.userInfo?.name || "N/A",
        a.doctorInfo?.service || "N/A",
        `${a.doctorInfo?.firstName || ""} ${a.doctorInfo?.lastName || ""}`,
        a.doctorInfo?.clinicDay?.join(", ") || "N/A",
        a.schedule_date ? moment(a.schedule_date).format("DD-MM-YYYY") : "Not set",
        a.status,
      ]),
    });
    doc.save("appointments.pdf");
    toast.success("Exported to PDF");
  };

  /* ── Cancel handlers ── */
  const openCancelModal = (record) => {
    setSelectedRecord(record);
    setCancelModalVisible(true);
    form.resetFields();
  };

  const closeCancelModal = () => {
    setCancelModalVisible(false);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      setConfirmLoading(true);
      dispatch(showLoading());
      const values = await form.validateFields();
      const { cancelComment } = values;
      if (!cancelComment?.trim()) {
        toast.error("Please enter a cancellation comment");
        setConfirmLoading(false);
        dispatch(hideLoading());
        return;
      }
      await axios.post(
        `/api/admin/cancel-appointment/${selectedRecord._id}`,
        { cancelComment },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      toast.success("Appointment canceled successfully");
      closeCancelModal();
      getAppointmentData();
    } catch {
      toast.error("Something went wrong");
    } finally {
      setConfirmLoading(false);
      dispatch(hideLoading());
    }
  };

  /* ── Summary counts ── */
  const counts = appointments.reduce((acc, a) => {
    acc[a.status] = (acc[a.status] || 0) + 1;
    return acc;
  }, {});

  /* ── Columns ── */
  const columns = [
    {
      title: "Patient",
      key: "patient",
      render: (_, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar size={32} style={{ background: `linear-gradient(135deg,${EMERALD},${TEAL})`, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            {r.userInfo?.name?.[0]?.toUpperCase()}
          </Avatar>
          <Text strong style={{ fontSize: 13, color: SLATE }}>{r.userInfo?.name || "N/A"}</Text>
        </div>
      ),
      sorter: (a, b) => (a.userInfo?.name || "").localeCompare(b.userInfo?.name || ""),
      sortOrder: sortedInfo.columnKey === "patient" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Service",
      key: "service",
      render: (_, r) => <Text style={{ fontSize: 13, color: SLATE_MID }}>{r.doctorInfo?.service || "—"}</Text>,
      filters: [...new Set(appointments.map(a => a.doctorInfo?.service))].filter(Boolean).map(s => ({ text: s, value: s })),
      filteredValue: filteredInfo.service || null,
      onFilter: (value, record) => record.doctorInfo?.service === value,
      sorter: (a, b) => (a.doctorInfo?.service || "").localeCompare(b.doctorInfo?.service || ""),
      sortOrder: sortedInfo.columnKey === "service" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Location",
      key: "location",
      render: (_, r) => <Text style={{ fontSize: 13, color: SLATE_MID }}>{r.doctorInfo?.location || "—"}</Text>,
      filters: [...new Set(appointments.map(a => a.doctorInfo?.location))].filter(Boolean).map(s => ({ text: s, value: s })),
      filteredValue: filteredInfo.location || null,
      onFilter: (value, record) => record.doctorInfo?.location === value,
      sorter: (a, b) => (a.doctorInfo?.location || "").localeCompare(b.doctorInfo?.location || ""),
      sortOrder: sortedInfo.columnKey === "location" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Scheduled Date",
      key: "schedule_date",
      render: (_, r) =>
        (r.status === "approved" || r.status === "follow-up") && r.schedule_date
          ? <Text style={{ fontSize: 12, color: SLATE_MID }}>{moment(r.schedule_date).format("ddd, MMM D, YYYY")}</Text>
          : <Text style={{ fontSize: 12, color: MUTED }}>—</Text>,
      sorter: (a, b) => moment(a.schedule_date || 0).unix() - moment(b.schedule_date || 0).unix(),
      sortOrder: sortedInfo.columnKey === "schedule_date" ? sortedInfo.order : null,
    },
    {
      title: "Status",
      key: "status",
      render: (_, r) => {
        const cfg = getStatusCfg(r.status);
        return (
          <span className="ac-badge" style={{ background: cfg.bg, color: cfg.color }}>
            {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
          </span>
        );
      },
      filters: [
        { text: "Approved", value: "approved" },
        { text: "Canceled", value: "canceled" },
      ],
      filteredValue: filteredInfo.status || null,
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, r) => {
        if (r.status === "approved") {
          return (
            <Button className="ac-cancel-btn" icon={<StopOutlined />} onClick={() => openCancelModal(r)}>
              Cancel
            </Button>
          );
        }
        if (r.status === "canceled") {
          return (
            <span className="ac-badge" style={{ background: "#fee2e2", color: "#dc2626" }}>
              <CloseCircleOutlined style={{ marginRight: 4 }} />Canceled
            </span>
          );
        }
        return null;
      },
    },
  ];

  return (
    <Layout>
      <div className="ac-root">
        <div className="ac-wrap">

          {/* Hero */}
          <div className="ac-hero ac-fade">
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="ac-eyebrow"><StopOutlined style={{ marginRight: 5 }} />Appointments</div>
              <Title level={2} style={{ margin: "6px 0 4px", fontFamily: "'Sora',sans-serif", color: WHITE, fontWeight: 800 }}>
                Cancel Appointments
              </Title>
              <Text style={{ color: MUTED, fontSize: 14 }}>
                Review approved appointments and cancel with a documented reason
              </Text>
              <div className="ac-hero-badges">
                <div className="ac-hero-badge"><CalendarOutlined style={{ marginRight: 5 }} />{appointments.length} Total</div>
                {counts.approved && <div className="ac-hero-badge" style={{ background: `${TEAL_LIGHT}cc`, color: EMERALD }}>✓ {counts.approved} Approved</div>}
                {counts.canceled && <div className="ac-hero-badge" style={{ background: "#fee2e2cc", color: "#dc2626" }}>✗ {counts.canceled} Canceled</div>}
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="ac-toolbar ac-fade ac-fade-1">
            <Input
              className="ac-search"
              prefix={<SearchOutlined style={{ color: MUTED }} />}
              placeholder="Search patient..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              allowClear
            />
            <Select
              className="ac-status-select"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 160 }}
              options={[
                { value: "All",        label: "All Statuses" },
                { value: "pending",    label: "Pending"      },
                { value: "approved",   label: "Approved"     },
                { value: "rejected",   label: "Rejected"     },
                { value: "follow-up",  label: "Follow-up"    },
                { value: "completed",  label: "Completed"    },
                { value: "canceled",   label: "Canceled"     },
              ]}
            />
            <Button className="ac-btn ac-btn-excel" icon={<FileExcelOutlined />} onClick={exportToCSV}>Excel</Button>
            <Button className="ac-btn ac-btn-pdf"   icon={<FilePdfOutlined />}   onClick={exportToPDF}>PDF</Button>
            <Button className="ac-btn" icon={<FilterOutlined />} onClick={() => setFilteredInfo({})}>Filters</Button>
            <Button className="ac-btn" icon={<ClearOutlined />}  onClick={() => { setFilteredInfo({}); setSortedInfo({}); }}>Reset</Button>
          </div>

          {/* Table */}
          <div className="ac-table-wrap ac-fade ac-fade-2">
            <Table
              columns={columns}
              dataSource={filteredData.map(a => ({ ...a, key: a._id }))}
              onChange={handleTableChange}
              scroll={{ x: "max-content" }}
              pagination={{ pageSize: 10, showSizeChanger: false }}
            />
          </div>

          {/* Cancel Modal */}
          <Modal
            className="ac-modal"
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: "#fee2e2", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <StopOutlined />
                </div>
                <span>Cancel Appointment</span>
              </div>
            }
            open={cancelModalVisible}
            onCancel={closeCancelModal}
            footer={[
              <Button key="back" className="ac-modal-close-btn" onClick={closeCancelModal}>
                Keep Appointment
              </Button>,
              <Button key="submit" className="ac-modal-confirm-btn" loading={confirmLoading} onClick={handleSubmit} icon={<StopOutlined />}>
                Confirm Cancellation
              </Button>,
            ]}
            width={540}
            destroyOnClose
          >
            {/* Appointment info strip */}
            {selectedRecord && (
              <div className="ac-appt-strip">
                <div className="ac-strip-row">
                  <div className="ac-strip-icon" style={{ background: TEAL_LIGHT, color: EMERALD }}>
                    <UserOutlined />
                  </div>
                  <div>
                    <Text style={{ fontSize: 11, color: MUTED, display: "block" }}>Patient</Text>
                    <Text strong style={{ fontSize: 13, color: SLATE }}>{selectedRecord.userInfo?.name}</Text>
                  </div>
                </div>
                <div className="ac-strip-row">
                  <div className="ac-strip-icon" style={{ background: "#eff6ff", color: "#3b82f6" }}>
                    <MedicineBoxOutlined />
                  </div>
                  <div>
                    <Text style={{ fontSize: 11, color: MUTED, display: "block" }}>Service</Text>
                    <Text strong style={{ fontSize: 13, color: SLATE }}>{selectedRecord.doctorInfo?.service}</Text>
                  </div>
                </div>
                <div className="ac-strip-row">
                  <div className="ac-strip-icon" style={{ background: "#faf5ff", color: "#8b5cf6" }}>
                    <EnvironmentOutlined />
                  </div>
                  <div>
                    <Text style={{ fontSize: 11, color: MUTED, display: "block" }}>Location</Text>
                    <Text strong style={{ fontSize: 13, color: SLATE }}>{selectedRecord.doctorInfo?.location}</Text>
                  </div>
                </div>
                {selectedRecord.schedule_date && (
                  <div className="ac-strip-row">
                    <div className="ac-strip-icon" style={{ background: "#fee2e2", color: "#dc2626" }}>
                      <CalendarOutlined />
                    </div>
                    <div>
                      <Text style={{ fontSize: 11, color: MUTED, display: "block" }}>Scheduled Date</Text>
                      <Text strong style={{ fontSize: 13, color: "#dc2626" }}>
                        {moment(selectedRecord.schedule_date).format("dddd, MMMM D, YYYY")}
                      </Text>
                    </div>
                  </div>
                )}
              </div>
            )}

            <Form form={form} layout="vertical" requiredMark={false} preserve={false}>
              <Form.Item
                label="Cancellation Reason"
                name="cancelComment"
                rules={[{ required: true, message: "Please enter a cancellation reason" }]}
              >
                <Input.TextArea
                  rows={4}
                  placeholder="Enter the reason for canceling this appointment..."
                />
              </Form.Item>
            </Form>
          </Modal>

        </div>
      </div>
    </Layout>
  );
};

export default AppointmentCanceled;